/**
 * Local wall-clock (birth time as entered by the user, in the birthplace's
 * own historic local time) -> UTC instant conversion.
 *
 * Why this exists: `new Date(y, m, d, h, min)` always uses the *browser's*
 * local timezone, which is almost never the birth timezone. We need the
 * historically-correct UTC offset (including any DST that was in effect on
 * that specific date, in that specific IANA zone) for the birthplace —
 * exactly what docs/reference-calculator.md documented the reference
 * calculator (chartcreators.vedicastrologyreports.com) doing server-side
 * from land+city (see that doc's section 3, "Zeitzonen- & Sommerzeit-
 * Handhabung"). We do the same thing client-side using the built-in
 * `Intl.DateTimeFormat` timezone database (no extra library needed for the
 * offset math itself — see cityLookup.ts for how we get the IANA zone name
 * for a birthplace).
 */

/**
 * Converts a local wall-clock date/time in a given IANA timezone to the
 * equivalent UTC `Date`. Iterative because the UTC offset itself can change
 * across the conversion (DST transitions) — 2 iterations is enough since
 * offsets only ever shift by whole hours/half-hours and this converges
 * immediately once the offset guess is correct for the target moment.
 */
export function zonedTimeToUtc(
  year: number,
  month: number, // 1-12
  day: number,
  hour: number,
  minute: number,
  second: number,
  timeZone: string
): Date {
  let guess = new Date(Date.UTC(year, month - 1, day, hour, minute, second));
  const wantedUtcMs = guess.getTime();

  for (let i = 0; i < 3; i++) {
    const shownUtcMs = readAsIfUtc(guess, timeZone);
    const diff = wantedUtcMs - shownUtcMs;
    if (diff === 0) break;
    guess = new Date(guess.getTime() + diff);
  }
  return guess;
}

/** Formats `instant` as it appears in `timeZone`, then re-reads those wall-clock fields as if they were UTC (i.e. "what UTC timestamp has these same digits"). */
function readAsIfUtc(instant: Date, timeZone: string): number {
  const dtf = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  });
  const parts = dtf.formatToParts(instant);
  const get = (type: string) => parseInt(parts.find((p) => p.type === type)!.value, 10);
  return Date.UTC(get("year"), get("month") - 1, get("day"), get("hour"), get("minute"), get("second"));
}
